import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@istari-frontend/ui";

import { cn } from "~/utils/cn";
import { formatInfosecLevelName } from "~/utils/infosec";

export interface InfosecLevelOption {
  id: string;
  name: string;
  displayColor: string;
}

// Shown only while the platform default level is unknown: the resource then carries whatever the platform
// default is, exactly like a regular upload, and no assign call is made.
export const PLATFORM_DEFAULT_INFOSEC_VALUE = "platform-default";

interface ChatInfosecLevelSelectProps {
  id: string;
  // null = platform default.
  value: string | null;
  onChange: (infosecLevelId: string | null) => void;
  levels: InfosecLevelOption[];
  // The platform's default resource level when known; it is the preselected option.
  defaultLevel?: InfosecLevelOption | null;
  disabled?: boolean;
  // Dense sizing for the inline save card.
  compact?: boolean;
}

// The infosec picker shared by the save card, the save dialog and the share modal. Nothing is required:
// the platform default level is preselected and leaves classification to the platform, the same as
// uploading the file by hand; picking another level overrides it for this save only. The default level
// appears once in the list, under its own name.
export function ChatInfosecLevelSelect({
  id,
  value,
  onChange,
  levels,
  defaultLevel,
  disabled,
  compact,
}: ChatInfosecLevelSelectProps) {
  const defaultValue = defaultLevel?.id ?? PLATFORM_DEFAULT_INFOSEC_VALUE;
  // The list items follow the trigger's size so the dropdown text matches the field.
  const itemClassName = compact ? "text-xs" : undefined;
  const options =
    defaultLevel && !levels.some((level) => level.id === defaultLevel.id) ? [defaultLevel, ...levels] : levels;

  return (
    <div className={cn("flex flex-col", compact ? "gap-1" : "gap-1.5")}>
      <Label htmlFor={id} className="text-xs">
        Infosec level
      </Label>
      <Select
        value={value ?? defaultValue}
        onValueChange={(selected) => onChange(selected === defaultValue ? null : selected)}
        disabled={disabled}
      >
        <SelectTrigger id={id} size={compact ? "xs" : "default"} className={cn(compact && "w-full")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {!defaultLevel && (
            <SelectItem value={PLATFORM_DEFAULT_INFOSEC_VALUE} className={itemClassName}>
              Platform default
            </SelectItem>
          )}
          {options.map((level) => (
            <SelectItem key={level.id} value={level.id} className={itemClassName}>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: level.displayColor }} />
                <span>{formatInfosecLevelName(level.name)}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
